/**
 * sold-out-inquiry.js
 *
 * - Opens/closes the sold-out inquiry modal
 * - Moves modal to body to avoid stacking/z-index issues
 * - Allows normal Shopify contact form submit so CAPTCHA works
 * - Saves draft + restores modal after CAPTCHA/redirect return
 * - Cleans up Swym wishlist injections inside the modal
 */

(function () {
  const DRAFT_KEY = "soldOutInquiryDraft";
  const PENDING_KEY = "soldOutInquiryPending";
  const RETURN_URL_KEY = "soldOutInquiryReturnUrl";
  const MODAL_ID_KEY = "soldOutInquiryModalId";

  function ensureModalInBody(modal) {
    if (!modal) return;
    if (modal.parentElement === document.body) return;

    const placeholder = document.createComment("sold-out-inquiry-modal-placeholder");
    modal._soldOutInquiryPlaceholder = placeholder;
    modal.parentNode.insertBefore(placeholder, modal);
    document.body.appendChild(modal);
  }

  function restoreModalToPlaceholder(modal) {
    if (!modal) return;
    const placeholder = modal._soldOutInquiryPlaceholder;

    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.insertBefore(modal, placeholder);
      placeholder.parentNode.removeChild(placeholder);
    }

    modal._soldOutInquiryPlaceholder = null;
  }

  function clearDraft() {
    try {
      sessionStorage.removeItem(DRAFT_KEY);
      sessionStorage.removeItem(PENDING_KEY);
      sessionStorage.removeItem(RETURN_URL_KEY);
      sessionStorage.removeItem(MODAL_ID_KEY);
    } catch (_) {}
  }

  function fixSwymInModal(modal) {
    if (!modal) return;

    modal.querySelectorAll(".swym-atw-button-container").forEach((el) => el.remove());

    const submit = modal.querySelector("[data-sold-out-inquiry-submit]");
    const actions = modal.querySelector(".sold-out-inquiry-modal__actions");

    if (submit && actions && !actions.contains(submit)) {
      actions.appendChild(submit);
    }
  }

  function setModalOpen(modal) {
    if (!modal) return;

    modal.removeAttribute("hidden");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("sold-out-inquiry-modal-open");
    document.body.classList.add("sold-out-inquiry-modal-open");
  }

  function setModalClosed(modal) {
    if (!modal) return;

    modal.setAttribute("hidden", "hidden");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("sold-out-inquiry-modal-open");
    document.body.classList.remove("sold-out-inquiry-modal-open");
  }

  function ensureCaptchaNote(modal) {
    if (!modal) return;

    let note = modal.querySelector("[data-sold-out-inquiry-captcha-note]");
    if (note) return;

    note = document.createElement("p");
    note.setAttribute("data-sold-out-inquiry-captcha-note", "");
    note.className = "sold-out-inquiry-modal__captcha-note";
    note.textContent =
      "If you get a CAPTCHA, your inquiry will be sent once it’s completed successfully.";

    const submitBtn = modal.querySelector("[data-sold-out-inquiry-submit]");

    if (submitBtn && submitBtn.parentNode) {
      submitBtn.parentNode.insertBefore(note, submitBtn.nextSibling);
    } else {
      modal.appendChild(note);
    }
  }

  function clearInlineErrors(modal) {
    if (!modal) return;
    modal.querySelectorAll("[data-sold-out-inquiry-error]").forEach((el) => el.remove());
  }

  function showInlineMessage(modal, text) {
    if (!modal) return;

    let node = modal.querySelector("[data-sold-out-inquiry-error]");

    if (!node) {
      node = document.createElement("p");
      node.setAttribute("data-sold-out-inquiry-error", "");
      node.className = "sold-out-inquiry-modal__error";

      const submitBtn = modal.querySelector("[data-sold-out-inquiry-submit]");
      if (submitBtn && submitBtn.parentNode) {
        submitBtn.parentNode.insertBefore(node, submitBtn.nextSibling);
      } else {
        modal.appendChild(node);
      }
    }

    node.textContent = text;
  }

  function saveDraft(form) {
    try {
      const fd = new FormData(form);
      const saved = {};

      for (const [k, v] of fd.entries()) {
        if (typeof v === "string") saved[k] = v;
      }

      const modal = form.closest(".sold-out-inquiry-modal");

      if (modal && modal.id) {
        sessionStorage.setItem(MODAL_ID_KEY, modal.id);
      }

      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(saved));
      sessionStorage.setItem(PENDING_KEY, "1");
      sessionStorage.setItem(RETURN_URL_KEY, window.location.href);
    } catch (_) {}
  }

  function restoreDraftIntoModal(modal) {
    if (!modal) return;

    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;

      const draft = JSON.parse(raw);
      if (!draft || typeof draft !== "object") return;

      Object.keys(draft).forEach((name) => {
        const selectorName =
          window.CSS && CSS.escape ? CSS.escape(name) : name.replace(/"/g, '\\"');

        const el = modal.querySelector(`[name="${selectorName}"]`);
        if (el && "value" in el) el.value = draft[name];
      });
    } catch (_) {}
  }

  function openModal(modal) {
    if (!modal) return;

    ensureModalInBody(modal);
    setModalOpen(modal);
    clearInlineErrors(modal);
    ensureCaptchaNote(modal);
    fixSwymInModal(modal);

    let tries = 0;
    const iv = setInterval(() => {
      fixSwymInModal(modal);
      tries += 1;
      if (tries >= 10) clearInterval(iv);
    }, 100);
  }

  function closeModal(modal) {
    setModalClosed(modal);
    restoreModalToPlaceholder(modal);
  }

  (function restoreAfterCaptcha() {
    try {
      const pending = sessionStorage.getItem(PENDING_KEY);
      if (pending !== "1") return;

      sessionStorage.removeItem(PENDING_KEY);

      const params = new URLSearchParams(window.location.search);
      const contactPosted = params.get("contact_posted") === "true";

      const modalId = sessionStorage.getItem(MODAL_ID_KEY);
      sessionStorage.removeItem(MODAL_ID_KEY);

      const modal = modalId
        ? document.getElementById(modalId)
        : document.querySelector(".sold-out-inquiry-modal");

      if (!modal) return;

      openModal(modal);

      if (contactPosted) {
        try {
          sessionStorage.removeItem(DRAFT_KEY);
          sessionStorage.removeItem(RETURN_URL_KEY);
        } catch (_) {}

        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("contact_posted");
          window.history.replaceState({}, "", url.toString());
        } catch (_) {}

        const form = modal.querySelector("[data-sold-out-inquiry]");
        if (form) {
        form.hidden = true;
        form.style.display = "none";
        }

        let successNode = modal.querySelector("[data-sold-out-inquiry-success]");
        if (!successNode) {
        successNode = document.createElement("div");
        successNode.setAttribute("data-sold-out-inquiry-success", "");
        successNode.className = "sold-out-inquiry-modal__success";

        const panel = modal.querySelector(".sold-out-inquiry-modal__panel");
        if (panel) panel.appendChild(successNode);
        }

        successNode.textContent = "Inquiry sent successfully. We’ll get back to you shortly.";
      } else {
        restoreDraftIntoModal(modal);

        showInlineMessage(
          modal,
          "If Shopify shows a CAPTCHA, your inquiry will be sent once it’s completed successfully. If you’re not sure, you can submit again."
        );
      }

      const submitBtn = modal.querySelector("[data-sold-out-inquiry-submit]");
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute("aria-busy");
        submitBtn.textContent = "Submit Inquiry";
      }
    } catch (_) {}
  })();

  document.addEventListener("click", function (e) {
    const openBtn = e.target.closest("[data-sold-out-inquiry-open]");

    if (openBtn) {
      e.preventDefault();

      const id = openBtn.getAttribute("aria-controls");
      const modal = id ? document.getElementById(id) : null;

      openModal(modal);
      return;
    }

    const closeBtn = e.target.closest("[data-sold-out-inquiry-close]");

    if (closeBtn) {
      e.preventDefault();

      const modal = closeBtn.closest(".sold-out-inquiry-modal");
      closeModal(modal);
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;

    document
      .querySelectorAll(".sold-out-inquiry-modal:not([hidden])")
      .forEach(closeModal);
  });

  document.addEventListener("shopify:section:unload", function () {
    document
      .querySelectorAll(".sold-out-inquiry-modal:not([hidden])")
      .forEach(closeModal);
  });

  document.addEventListener("submit", function (e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const modal = form.closest(".sold-out-inquiry-modal");
    if (!modal) return;

    const submitBtn = form.querySelector("[data-sold-out-inquiry-submit]");
    if (!submitBtn) return;

    clearInlineErrors(modal);
    ensureCaptchaNote(modal);
    fixSwymInModal(modal);

    if (!form.checkValidity()) {
      e.preventDefault();
      form.reportValidity();
      return;
    }

    saveDraft(form);

    submitBtn.disabled = true;
    submitBtn.setAttribute("aria-busy", "true");
    submitBtn.textContent = "Submitting…";
  });
})();